import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi, type LeadFrtReport } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { formatLeadStatusLabel } from "@/lib/crmLeadStyles";
import { useAuth } from "@/contexts/AuthContext";
import { isCrmViewOnlyUser, shouldMaskLeadPhone } from "@/utils/crmAccess";
import { maskPhoneDisplay } from "@/utils/stringUtils";

type Props = {
  from: string;
  to: string;
  hospitalId?: string;
};

function fmtFrtMinutes(m: number | null | undefined): string {
  if (m == null || Number.isNaN(m)) return "—";
  if (m < 1) return "<1 min";
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

function fmtPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** Distinct strokes for Total + sales people (avoid purple/glow defaults). */
const WOW_LINE_COLORS = [
  "#0f172a", // Total — slate
  "#dc2626",
  "#2563eb",
  "#059669",
  "#d97706",
  "#0891b2",
  "#be123c",
  "#4f46e5",
  "#65a30d",
  "#ea580c",
];

export function LeadFrtTab({ from, to, hospitalId }: Props) {
  const { user } = useAuth();
  const crmViewOnly = isCrmViewOnlyUser(user);
  const [downloading, setDownloading] = useState(false);

  const reportParams = useMemo(() => {
    const p: Record<string, string> = { from, to };
    if (hospitalId) p.hospitalId = hospitalId;
    return p;
  }, [from, to, hospitalId]);

  const frtQuery = useQuery({
    queryKey: ["lead-frt-report", reportParams],
    queryFn: async () => (await leadsApi.getFrtReport(reportParams)).data.data as LeadFrtReport,
  });

  const data = frtQuery.data;
  const summary = data?.summary;
  const slaMinutes = data?.slaMinutes ?? 15;

  const chartRows = useMemo(() => {
    return (data?.bySalesPerson ?? [])
      .filter((r) => r.responded > 0)
      .map((r) => ({
        name: r.name.length > 18 ? `${r.name.slice(0, 16)}…` : r.name,
        fullName: r.name,
        avgFrt: r.avgFrtMinutes != null ? Math.round(r.avgFrtMinutes) : 0,
      }));
  }, [data?.bySalesPerson]);

  const wowSeries = useMemo(() => {
    const weekly = data?.weeklyAvgFrt ?? [];
    if (!weekly.length) return { rows: [] as Record<string, string | number | null>[], keys: [] as string[] };

    const staffOrder: string[] = [];
    const seen = new Set<string>();
    for (const w of weekly) {
      for (const sp of w.bySalesPerson) {
        if (!seen.has(sp.name)) {
          seen.add(sp.name);
          staffOrder.push(sp.name);
        }
      }
    }
    const keys = ["Total", ...staffOrder];
    const rows = weekly.map((w) => {
      const row: Record<string, string | number | null> = {
        weekLabel: w.weekLabel,
        weekKey: w.weekKey,
        Total: w.totalAvgFrtMinutes != null ? Math.round(w.totalAvgFrtMinutes) : null,
      };
      for (const name of staffOrder) {
        const sp = w.bySalesPerson.find((s) => s.name === name);
        row[name] = sp?.avgFrtMinutes != null ? Math.round(sp.avgFrtMinutes) : null;
      }
      return row;
    });
    return { rows, keys };
  }, [data?.weeklyAvgFrt]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await leadsApi.downloadFrtReport(reportParams);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lead-frt-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download FRT CSV");
    } finally {
      setDownloading(false);
    }
  };

  if (frtQuery.isLoading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading FRT report…</p>;
  }

  if (frtQuery.isError) {
    return (
      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
        Failed to load FRT report.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-3xl">
          First response time (FRT) is measured from when a lead enters the CRM until the assigned
          sales person&apos;s <strong>first touch</strong> (status change, activity, or assignment).
          Sarvam and other automated updates do not count. Office hours only (
          <strong>{data?.officeHoursLabel ?? "10:00–19:00 IST"}</strong>); nights excluded. SLA
          target: <strong>{slaMinutes} office minutes</strong>.
        </p>
        {!crmViewOnly && (
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={downloading}>
            <Download className="mr-2 h-4 w-4" />
            {downloading ? "Downloading…" : "Download CSV"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <SummaryCard label="Leads in range" value={String(summary?.totalLeads ?? 0)} />
        <SummaryCard
          label="Avg FRT"
          value={fmtFrtMinutes(summary?.avgFrtMinutes)}
          hint="Responded leads only · office hours"
        />
        <SummaryCard
          label="Median FRT"
          value={fmtFrtMinutes(summary?.medianFrtMinutes)}
          hint="Responded leads only · office hours"
        />
        <SummaryCard
          label={`Within ${slaMinutes} min`}
          value={fmtPct(summary?.withinSlaPct)}
          hint={`${summary?.withinSla ?? 0} of ${summary?.responded ?? 0} responded`}
        />
        <SummaryCard
          label="Awaiting response"
          value={String(summary?.pending ?? 0)}
          hint="No sales-person touch yet"
        />
        <SummaryCard
          label="SLA breached"
          value={String(summary?.overdue ?? 0)}
          hint={`>${slaMinutes} office min without sales touch`}
        />
      </div>

      {chartRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Average FRT by sales person</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={56}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} unit=" min" />
                <Tooltip
                  formatter={(v: number) => [`${v} min`, "Avg FRT"]}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ? String(payload[0].payload.fullName) : ""
                  }
                />
                <Bar dataKey="avgFrt" name="Avg FRT" fill="#dc2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {wowSeries.rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Average FRT week on week</CardTitle>
            <p className="text-xs text-muted-foreground font-normal">
              Responded leads only · office hours · weeks Mon–Sun IST · Total plus each active sales
              person (assigned, with ≥1 response in range)
            </p>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={wowSeries.rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} unit=" min" />
                <Tooltip
                  formatter={(v, name) => [
                    v == null || Number.isNaN(Number(v)) ? "—" : `${Math.round(Number(v))} min`,
                    String(name),
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {wowSeries.keys.map((key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={key}
                    stroke={WOW_LINE_COLORS[i % WOW_LINE_COLORS.length]}
                    strokeWidth={key === "Total" ? 2.5 : 1.75}
                    dot={{ r: key === "Total" ? 3 : 2 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sales person breakdown</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-[900px] w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="p-2 font-medium">Sales person</th>
                <th className="p-2 font-medium text-center">Leads</th>
                <th className="p-2 font-medium text-center">Responded</th>
                <th className="p-2 font-medium text-center">Pending</th>
                <th className="p-2 font-medium text-center text-red-700">SLA breached</th>
                <th className="p-2 font-medium text-center">Avg FRT</th>
                <th className="p-2 font-medium text-center">Median FRT</th>
                <th className="p-2 font-medium text-center">{`≤ ${slaMinutes} min`}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.bySalesPerson ?? []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">
                    No leads in the selected date range.
                  </td>
                </tr>
              ) : (
                (data?.bySalesPerson ?? []).map((row) => (
                  <tr key={row.staffId ?? "__unassigned__"} className="border-b hover:bg-slate-50/80">
                    <td className="p-2 font-medium">{row.name}</td>
                    <td className="p-2 text-center tabular-nums">{row.totalLeads}</td>
                    <td className="p-2 text-center tabular-nums">{row.responded}</td>
                    <td className="p-2 text-center tabular-nums">{row.pending}</td>
                    <td className="p-2 text-center tabular-nums text-red-700 font-medium">
                      {row.overdue}
                    </td>
                    <td className="p-2 text-center tabular-nums">{fmtFrtMinutes(row.avgFrtMinutes)}</td>
                    <td className="p-2 text-center tabular-nums">
                      {fmtFrtMinutes(row.medianFrtMinutes)}
                    </td>
                    <td className="p-2 text-center tabular-nums">{fmtPct(row.withinSlaPct)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lead-level detail</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="p-2 font-medium">Patient</th>
                <th className="p-2 font-medium">Phone</th>
                <th className="p-2 font-medium">Owner</th>
                <th className="p-2 font-medium">Status</th>
                <th className="p-2 font-medium">Created</th>
                <th className="p-2 font-medium">First sales touch</th>
                <th className="p-2 font-medium text-center">FRT</th>
                <th className="p-2 font-medium text-center">SLA</th>
              </tr>
            </thead>
            <tbody>
              {(data?.leads ?? []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">
                    No leads in range.
                  </td>
                </tr>
              ) : (
                (data?.leads ?? []).map((lead) => (
                  <tr
                    key={lead.leadId}
                    className={`border-b ${lead.overdue ? "bg-red-50" : "hover:bg-slate-50/80"}`}
                  >
                    <td className="p-2 font-medium">{lead.patientName}</td>
                    <td className="p-2 tabular-nums">
                      {shouldMaskLeadPhone(user) ? maskPhoneDisplay(lead.phone) : lead.phone}
                    </td>
                    <td className="p-2">{lead.assignedToName}</td>
                    <td className="p-2">{formatLeadStatusLabel(lead.status)}</td>
                    <td className="p-2 text-xs whitespace-nowrap">
                      {new Date(lead.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                    </td>
                    <td className="p-2 text-xs whitespace-nowrap">
                      {lead.firstResponseAt
                        ? new Date(lead.firstResponseAt).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          })
                        : "—"}
                    </td>
                    <td className="p-2 text-center tabular-nums">{fmtFrtMinutes(lead.frtMinutes)}</td>
                    <td className="p-2 text-center">
                      {lead.overdue ? (
                        <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 border border-red-200">
                          Breached
                        </span>
                      ) : lead.frtMinutes != null ? (
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold border ${
                            lead.frtMinutes <= slaMinutes
                              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                              : "bg-amber-100 text-amber-900 border-amber-200"
                          }`}
                        >
                          {lead.frtMinutes <= slaMinutes ? "OK" : "Late"}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Pending</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
